import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './tickets.css';
import heroImg from '../assets/heroimg.png';
import arrowIcon from '../assets/arrow.png';

gsap.registerPlugin(ScrollTrigger);

const ticketPlans = [
  {
    name: 'Visitor Pass',
    price: '$50',
    text: 'Perfect for industry professionals',
    theme: 'dark',
    button: 'Select Pass',
    benefits: [
      'Full exhibition floor access',
      'Networking opportunities',
      'Product demonstrations',
      'Digital catalog access',
      'Event schedule & map',
    ],
  },
  {
    name: 'VIP Pass',
    price: '$150',
    text: 'Premium access & exclusive perks',
    theme: 'light',
    button: 'Select VIP Pass',
    popular: true,
    benefits: [
      'All Visitor Pass benefits',
      'VIP lounge access',
      'Priority seating at seminars',
      'Exclusive networking sessions',
      'Complimentary refreshments',
      'Fast-track entry',
      'Meeting room booking access',
      'Premium event materials',
    ],
  },
];

const features = [
  {
    title: 'Secure Payment',
    text: 'SSL encrypted checkout process',
    icon: '01',
  },
  {
    title: 'Instant Confirmation',
    text: 'Receive your ticket via email immediately',
    icon: '02',
  },
  {
    title: 'Group Discounts',
    text: 'Save 20% on orders of 10+ tickets',
    icon: '03',
  },
];

const Tickets = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const ctx = gsap.context(() => {
      gsap.from('.ticket-hero-item', {
        y: 46,
        opacity: 0,
        filter: 'blur(8px)',
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.12,
      });

      gsap.from('.ticket-card', {
        y: 80,
        rotateX: 8,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        stagger: 0.15,
        scrollTrigger: {
          trigger: '.ticket-cards',
          start: 'top 82%',
          once: true,
        },
      });

      gsap.from('.ticket-feature', {
        y: 46,
        opacity: 0,
        duration: 0.75,
        ease: 'back.out(1.4)',
        stagger: 0.12,
        scrollTrigger: {
          trigger: '.ticket-feature-grid',
          start: 'top 86%',
          once: true,
        },
      });

      gsap.utils.toArray('[data-ticket-reveal]').forEach((item) => {
        gsap.fromTo(
          item,
          { y: 58, filter: 'blur(8px)' },
          {
            y: 0,
            filter: 'blur(0px)',
            duration: 0.85,
            ease: 'power3.out',
            immediateRender: false,
            scrollTrigger: {
              trigger: item,
              start: 'top 86%',
              once: true,
            },
          }
        );
      });

      gsap.to('.ticket-photo', {
        yPercent: -12,
        ease: 'none',
        scrollTrigger: {
          trigger: '.tickets-form-section',
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });

      gsap.to('.ticket-float-ring', {
        rotate: 360,
        duration: 24,
        repeat: -1,
        ease: 'none',
      });
    }, page);

    return () => ctx.revert();
  }, []);

  return (
    <main className="tickets-page" ref={pageRef}>
      <section className="tickets-hero">
        <div className="ticket-photo-wrap">
          <img src={heroImg} alt="Textile machinery exhibition" className="ticket-photo" />
        </div>
        <div className="tickets-hero-overlay" />
        <div className="ticket-float-ring" aria-hidden="true" />

        <div className="tickets-hero-content">
          <span className="tickets-badge ticket-hero-item">April 20-23, 2026 / Cairo, Egypt</span>
          <h1 className="ticket-hero-item">Secure Your Access</h1>
          <p className="ticket-hero-item">
            Join 8,500+ industry professionals at the region's premier textile machinery exhibition.
          </p>
        </div>
      </section>

      <section className="passes-section">
        <div className="tickets-heading" data-ticket-reveal>
          <span className="tickets-badge">Choose Your Pass</span>
          <h2>Choose Your Pass</h2>
          <p>Select the pass that best fits your needs. All passes include full access to the exhibition floor.</p>
        </div>

        <div className="ticket-cards" data-ticket-reveal>
          {ticketPlans.map((plan) => (
            <article className={`ticket-card ticket-card-${plan.theme}`} key={plan.name}>
              {plan.popular && <div className="popular-ribbon">Most Popular</div>}
              <div className="ticket-card-body">
                <span className="ticket-mini-mark">{plan.popular ? 'VIP' : 'VIS'}</span>
                <h3>{plan.name}</h3>
                <p>{plan.text}</p>
                <div className="ticket-price">
                  <strong>{plan.price}</strong>
                  <span>/ pass</span>
                </div>
                <ul>
                  {plan.benefits.map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                  ))}
                </ul>
                <button type="button" className="ticket-btn">
                  {plan.button}
                  <img src={arrowIcon} alt="" aria-hidden="true" />
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="ticket-feature-grid" data-ticket-reveal>
          {features.map((feature) => (
            <article className="ticket-feature" key={feature.title}>
              <span>{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="tickets-form-section">
        <div className="tickets-form-heading" data-ticket-reveal>
          <span className="tickets-badge">Registration</span>
          <h2>Complete Your Registration</h2>
          <p>Fill in your details below to secure your ticket</p>
        </div>

        <form className="ticket-form" data-ticket-reveal>
          <div className="form-grid">
            <label>
              Full Name *
              <input type="text" placeholder="Enter your full name" />
            </label>
            <label>
              Email Address *
              <input type="email" placeholder="your@email.com" />
            </label>
            <label>
              Phone Number *
              <input type="tel" placeholder="+20 xxx xxx xxxx" />
            </label>
            <label>
              Company Name
              <input type="text" placeholder="Your company" />
            </label>
          </div>

          <label>
            Select Ticket Type *
            <select defaultValue="">
              <option value="" disabled>Choose your pass</option>
              <option>Visitor Pass - $50</option>
              <option>VIP Pass - $150</option>
            </select>
          </label>

          <p className="secure-note">Your information is secure. We use industry-standard encryption to protect your data.</p>

          <button type="button" className="payment-btn">
            Proceed to Payment
            <img src={arrowIcon} alt="" aria-hidden="true" />
          </button>
        </form>

        <p className="terms-copy">
          By completing this registration, you agree to our Terms & Conditions and Privacy Policy.
        </p>
      </section>
    </main>
  );
};

export default Tickets;
